import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PermissionAwareState } from './PermissionAwareState';

/**
 * ParticipantTransportTab — read-only participant-filtered transport trip logs.
 *
 * Reuses existing /api/transport/logs?participant_id endpoint (already supports
 * participant filter). No claim, no reimbursement, no invoice, no external
 * submission action. Navigation and display only.
 *
 * State discipline (approved 403 UX follow-up):
 *   - EMPTY  → 200 OK with zero records
 *   - DENIED → 401 or 403 from backend
 *   - ERROR  → network / 5xx / malformed response
 * The original HTTP status is preserved via console.error and never suppressed.
 */
export function ParticipantTransportTab({ participantId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchState, setFetchState] = useState('ok'); // 'ok' | 'empty' | 'denied' | 'error'

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setFetchState('ok');
    api.get(`/transport/logs?participant_id=${participantId}`)
      .then((r) => {
        if (cancelled) return;
        const items = r?.data?.data || [];
        const arr = Array.isArray(items) ? items : [];
        setLogs(arr);
        setFetchState(arr.length === 0 ? 'empty' : 'ok');
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status ?? null;
        // Preserve original HTTP status for diagnostics; do NOT suppress.
        console.error('[ParticipantTransportTab] fetch failed', { httpStatus: status });
        if (status === 401 || status === 403) {
          setFetchState('denied');
        } else {
          setFetchState('error');
        }
        setLogs([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  if (loading) {
    return (
      <div
        className="p-6 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
        data-testid="participant-transport-loading"
      >
        Loading transport bookings…
      </div>
    );
  }
  if (fetchState === 'denied') {
    return <PermissionAwareState status="denied" testIdBase="participant-transport" />;
  }
  if (fetchState === 'error') {
    return <PermissionAwareState status="error" testIdBase="participant-transport" onRetry={load} />;
  }
  if (fetchState === 'empty') {
    return (
      <PermissionAwareState
        status="empty"
        testIdBase="participant-transport"
        emptyText="No transport bookings found for this participant."
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="participant-transport-tab">
      <h3 className="text-sm font-semibold" data-testid="participant-transport-count">
        Transport Bookings ({logs.length})
      </h3>
      <div className="space-y-2">
        {logs.map((t) => {
          const id = t.id || t._id;
          return (
            <Card key={id} data-testid={`participant-transport-row-${id}`}>
              <CardContent className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {t.purpose || t.trip_purpose || t.trip_type || 'Trip'}
                    {(t.start_time || t.trip_date || t.created_at)
                      ? <span className="text-xs text-muted-foreground ml-2">{String(t.start_time || t.trip_date || t.created_at).slice(0, 10)}</span>
                      : null}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {(t.distance_km ?? t.km) != null ? <span>{t.distance_km ?? t.km} km</span> : <span>—</span>}
                    {t.vehicle_kind ? <span className="ml-2 capitalize">· {t.vehicle_kind}</span> : null}
                  </p>
                </div>
                <Badge variant="outline" className="text-[10px]">{t.status || '—'}</Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default ParticipantTransportTab;
